import classnames from 'classnames';
import { cloneDeep, get } from 'lodash/fp';
import React, { MouseEvent, ReactNode, useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../../../../../common/buttons/DeprecatedButton/DeprecatedButton';
import {
  SortableContainer,
  SortableElement,
} from '../../../../../../common/lib/react-sortable-hoc';
import { PathGraphContext } from '../../../../../../common/widgets/common/PathGraph/PathGraph';
import { useButtonCRUDActions } from '@mm/state/actions/ButtonCRUDActions';
import { Entry } from '@mm/state/actions/EntryCRUDActions';
import ButtonModel, {
  ButtonType,
  ButtonRequestModel,
} from '@mm/state/model/ButtonModel';
import WidgetModel from '@mm/state/model/WidgetModel';
import {
  closeModal,
  openButtonConfigFormModal,
} from '@mm/state/modules/modalsState/actions';
import { MAX_ALLOWED_BTNS } from '@mm/constants/widgets';
import { makeButton } from '../../../requestModels/widgets/buttonStackWidget/Button';
import { ButtonComponent } from '../buttons/ButtonComponent/ButtonComponent';
import { CreateEditButtonArea } from '../EditButtonArea/EditButtonArea';
import './ButtonListEditor.scss';
import { ExclamationIcon } from '@mm/icons/general';
import { colors } from '../../../../beta/styles/colors';

const ButtonsContainer = SortableContainer(
  ({
    children,
    ...props
  }: {
    children: ReactNode;
    props: ButtonListEditorProps;
  }) => <div {...props}>{children}</div>,
);

const DnDButtonComponent = SortableElement(ButtonComponent);

type ListButtonProps = {
  index: number;
  onClickButton: (index: number) => void;
  onDeleteButton: (index: number) => void;
  mainMenuSelected: boolean;
  totalButtons: number;
  button: ButtonModel;
  buttonClass?: string;
  disabled?: boolean;
  renderPathIcon?: boolean;
};

const ListButton = (props: ListButtonProps) => {
  const {
    index,
    onClickButton,
    onDeleteButton,
    button,
    disabled,
    mainMenuSelected,
    totalButtons,
    buttonClass,
    renderPathIcon,
  } = props;

  const classList = classnames('button-list-editor__button-container', {
    'button-list-editor--last-button': index === totalButtons - 1,
  });

  return (
    <div className={classList}>
      {disabled && (
        <ExclamationIcon
          className="button-list-editor__warning-icon"
          color={colors.orange}
        />
      )}

      <DnDButtonComponent
        className={classnames(
          'button-list-editor__justify-button',
          buttonClass,
        )}
        index={index}
        key={button.id}
        onClick={onClickButton(index)}
        onDelete={onDeleteButton(index)}
        entity={button}
        mainMenuSelected={mainMenuSelected}
        withBorders={false}
        renderPathIcon={renderPathIcon}
      />
    </div>
  );
};

const AddButton = ({
  onClick,
  disabled,
}: {
  onClick?: (e?: MouseEvent<HTMLDivElement>) => void;
  disabled?: boolean;
}) => {
  return (
    <div
      className={classnames('button-list-editor__new-add-button', {
        'text-widget__add-button--disabled': disabled,
      })}
      onClick={onClick}
    >
      + Add Button
    </div>
  );
};

type ButtonListEditorProps = {
  disabledButtonTypes?: ButtonType[];
  widget: WidgetModel;
  entry: Entry;
  maxButtons: number;
  buttons: ButtonModel[];
  useModal?: boolean;
  buttonClass?: string;
  useNewButton?: boolean;
  setUnsavedEntryButton?: (unsavedButton: ButtonModel | undefined) => void;
  usedInSlideout?: boolean;
};

export const ButtonListEditor = ({
  buttons = [],
  maxButtons = MAX_ALLOWED_BTNS,
  widget,
  entry,
  useModal = false,
  buttonClass,
  disabledButtonTypes,
  useNewButton = false,
  setUnsavedEntryButton,
  usedInSlideout,
}: ButtonListEditorProps) => {
  const mainMenuSelected = useSelector(
    get('view.chatbotEditor.mainMenuSelected'),
  );
  const dispatch = useDispatch();

  const [showAddButton, setShowAddButton] = useState(false);
  const defaultButton = makeButton({
    type: ButtonType.PAGE,
    name: '',
    sortOrder: -1,
    configExtendObject: {
      pageId: -1,
    },
  });
  const [selectedButton, setSelectedButton] =
    useState<Partial<ButtonModel>>(defaultButton);
  const [buttonIndex, setButtonIndex] = useState(-1);
  const [editButtonMode, setIsEditButtonMode] = useState(false);

  const buttonCRUDActions = useButtonCRUDActions();

  const { deselectWidgetButtons, removeNodeAndSuccessors } =
    useContext(PathGraphContext);

  let sortedButtons: ButtonModel[] = [];

  const onCreateButtonHandler = (button: ButtonModel) => {
    const maxSortOrder = Math.max(...buttons.map(b => b.sortOrder), -1);
    buttonCRUDActions.createEntry(widget.id, entry.id, {
      ...button,
      sortOrder: maxSortOrder + 1,
    } as ButtonRequestModel);
    if (useModal) {
      dispatch(closeModal());
    } else {
      setShowAddButton(false);
      setIsEditButtonMode(false);
    }
  };

  const onUpdateButtonHandler = (_: number, button: ButtonModel) => {
    buttonCRUDActions.updateEntry(widget.id, entry.id, { ...button });

    if (useModal) {
      dispatch(closeModal());
    } else {
      setShowAddButton(false);
      setIsEditButtonMode(false);
    }
  };

  const onClickButtonHandler = (index: number) => () => {
    const selectedButton = { ...sortedButtons[index] };

    deselectWidgetButtons?.(buttons);

    // Note: currently I do nto see it used by the Gallery widget, so I'm not implementing
    // updates to PathGraph here. Please make sure to introduce the code to update PathGraph
    // if your changes to the Gallery widget start use this modal.
    if (useModal) {
      dispatch(
        openButtonConfigFormModal(
          selectedButton,
          index,
          onCreateButtonHandler,
          onUpdateButtonHandler,
          widget,
        ),
      );
      return;
    }
    setSelectedButton(selectedButton);
    setButtonIndex(index);
    setIsEditButtonMode(true);
    setShowAddButton(false);
    // Note: needed to unmount CreateEditButtonArea, as otherwise resetting all the state it too complicated
    setTimeout(() => setShowAddButton(true), 50);
  };

  const onClickAddButtonHandler = () => {
    const maxSortOrder = Math.max(...buttons.map(b => b.sortOrder), 0);
    setShowAddButton(false);
    const selectedButton = makeButton({
      type: ButtonType.PAGE,
      name: '',
      sortOrder: maxSortOrder + 1,
      configExtendObject: {
        pageId: -1,
      },
    });
    if (useModal) {
      dispatch(
        openButtonConfigFormModal(
          selectedButton,
          -1,
          onCreateButtonHandler,
          onUpdateButtonHandler,
          widget,
        ),
      );
      return;
    }

    setShowAddButton(true);

    setSelectedButton(selectedButton);
    setButtonIndex(-1);
  };

  const onDeleteButtonHandler = (index: number) => () => {
    const button = buttons[index];
    if (button) {
      buttonCRUDActions.deleteEntry(widget.id, entry.id, { ...button });

      removeNodeAndSuccessors?.({
        id: button.id,
        type: 'button',
        actionType: 'deleteSuccessors',
      });
    }
  };

  const onSortEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    if (oldIndex === newIndex) return;

    const buttons = [...sortedButtons];

    const { sortOrder: newSortOrder } = buttons[newIndex];
    const targetButton = { ...buttons[oldIndex] };

    buttonCRUDActions.updateEntry(widget.id, entry.id, {
      ...targetButton,
      sortOrder: newSortOrder,
    });

    if (oldIndex < newIndex) {
      for (let i = oldIndex + 1; i <= newIndex; i += 1) {
        const button = buttons[i];

        buttonCRUDActions.updateEntry(widget.id, entry.id, {
          ...button,
          sortOrder: button.sortOrder - 1,
        });
      }
    }

    if (oldIndex > newIndex) {
      for (let i = newIndex; i < oldIndex; i += 1) {
        const button = buttons[i];

        buttonCRUDActions.updateEntry(widget.id, entry.id, {
          ...button,
          sortOrder: button.sortOrder + 1,
        });
      }
    }
  };

  const getButtons = () => {
    const copiedButtons = buttons.map(cloneDeep);
    copiedButtons.sort((a, b) => a.sortOrder - b.sortOrder);
    sortedButtons = copiedButtons;

    return sortedButtons;
  };

  const onCLoseButtonArea = (setIsEditButton: boolean) => {
    setShowAddButton(false);
    setIsEditButton && setIsEditButtonMode(false);
    deselectWidgetButtons?.(buttons);
  };

  const buttonsList = getButtons();
  const totalButtons = buttons.length;
  const canAddButton = totalButtons < maxButtons;

  return (
    <div className="button-list-editor__outer-container">
      <ButtonsContainer
        className="button-list-editor__buttons-container"
        distance={15}
        lockAxis="y"
        lockOffset={0}
        lockToContainerEdges
        onSortEnd={onSortEnd}
      >
        {buttonsList.map((button, index) => (
          <ListButton
            buttonClass={classnames(buttonClass, {
              'button-list-editor__btn--disabled':
                disabledButtonTypes?.includes(button.type),
            })}
            disabled={disabledButtonTypes?.includes(button.type)}
            key={button.id}
            index={index}
            onClickButton={onClickButtonHandler}
            onDeleteButton={onDeleteButtonHandler}
            button={button}
            mainMenuSelected={mainMenuSelected}
            totalButtons={totalButtons}
            renderPathIcon={button.type === ButtonType.PAGE}
          />
        ))}
      </ButtonsContainer>

      <div className="text-widget__add-button-wrapper">
        {editButtonMode && showAddButton && (
          <CreateEditButtonArea
            disabledButtonTypes={disabledButtonTypes}
            button={selectedButton}
            buttonIndex={buttonIndex}
            onCreateButtonHandler={onCreateButtonHandler}
            onUpdateButtonHandler={onUpdateButtonHandler}
            closeButtonArea={() => onCLoseButtonArea(true)}
            setUnsavedEntryButton={setUnsavedEntryButton}
            widget={widget}
            usedInSlideout={usedInSlideout}
          />
        )}
      </div>

      {canAddButton && (
        <div className="button-list-editor__inner-container">
          {!useNewButton ? (
            <Button
              purple
              invert
              block
              className="button-list-editor__add-button"
              onClick={onClickAddButtonHandler}
              disabled={!canAddButton || showAddButton}
            >
              <i className="fa fa-plus-circle fa-lg warning" />
              &nbsp; Add button
            </Button>
          ) : (
            <AddButton
              onClick={onClickAddButtonHandler}
              disabled={!canAddButton || showAddButton}
            />
          )}
        </div>
      )}
      <div className="text-widget__add-button-wrapper">
        {!editButtonMode && showAddButton && (
          <CreateEditButtonArea
            disabledButtonTypes={disabledButtonTypes}
            button={selectedButton}
            buttonIndex={buttonIndex}
            onCreateButtonHandler={onCreateButtonHandler}
            onUpdateButtonHandler={onUpdateButtonHandler}
            closeButtonArea={() => onCLoseButtonArea(false)}
            setUnsavedEntryButton={setUnsavedEntryButton}
            widget={widget}
            usedInSlideout={usedInSlideout}
          />
        )}
      </div>
    </div>
  );
};
